import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { ICategory, initialValues, registrationSchema } from './constants';
import { Button } from '../../../components/Button/Button';
import FieldInput from '../../pro/createEditProfile/formFragments/FieldInput';
import FormRow from '../../pro/createEditProfile/formFragments/row';
import { CustomTextArea } from '../../../components/fields/CustomTextArea/CustomTextArea';
import { FieldSelect } from '../../pro/createEditProfile/formFragments/FieldSelect';
import { ParentSelector } from './ParentSelector';

interface Props {
  isLoading: boolean;
  categoriesList: ICategory[];
  data?: ICategory;
  onSubmit: (payload: Partial<ICategory>) => Promise<void>;
  onClose?: () => void;
}

export function CategoryForm({
  isLoading,
  data,
  onClose,
  onSubmit,
  ...rest
}: Props) {
  const [category, setCategory] = useState(data);
  const [parentId, setParentId] = useState(data?.parent_id || null);

  const submit = async (values: any) => {
    const payload = {
      ...values,
      parent_id: parentId || null,
    };
    onSubmit(payload);
  };

  useEffect(() => {
    setCategory(data);
    setParentId(data?.parent_id || null);
  }, [data]);

  const formInitValues = useMemo(() => {
    if (!category) return initialValues;
    const { name, heading, status } = category;
    return { name, heading, status };
  }, [category]);

  return (
    <Dialog
      open
      fullWidth
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          '@media (max-width: 599.95px)': {
            m: 1.5,
            width: 'calc(100% - 24px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Typography
          fontFamily="CircularStdBlack"
          sx={{ fontSize: { xs: 18, sm: 22 } }}
        >
          {category ? 'Edit' : 'Add'} Category
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 2 } }}>
        <Box>
          <Formik
            initialValues={formInitValues}
            validationSchema={registrationSchema}
            onSubmit={submit}
          >
            <Form>
              <Box className="flex flex-col gap-6 bg-surface w-full mb-4">
                <FormRow count={1}>
                  <FieldInput name="name" placeholder="Name" required />
                </FormRow>
                <FormRow count={1}>
                  <ParentSelector
                    {...{
                      ...rest,
                      parentId,
                      setParentId,
                      currentCategoryId: category?.id,
                    }}
                  />
                </FormRow>
                <FormRow count={1}>
                  <CustomTextArea
                    name="heading"
                    label="Heading Text"
                    placeholder="Heading Text..."
                    rows={5}
                  />
                </FormRow>
                <FormRow count={1}>
                  <FieldSelect
                    name="status"
                    placeholder="status"
                    options={['draft', 'published']}
                  />
                </FormRow>
              </Box>

              <Box className="flex items-center flex-end w-full">
                <Button
                  size="large"
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </Form>
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
